import { useAction, useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import { lazy, Suspense } from 'react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { tradingPairAddressAtom } from '@root/features/trade/trading'
import { tradingPairStateAtom } from '@root/features/trade/trading/atoms/reatom-trading-pair'
import { ChevronLeftIcon, mediaMaxTablet } from '@root/shared/ui-kit'
import { OrdersTablesSkeleton } from '@root/widgets/trade/tables/orders-table/ui/orders-tables-skeleton'
// From its own module, not the barrel beside it: `export * from
// './trade-chart'` there made this static import reach the chart as well,
// and rolldown said so — INEFFECTIVE_DYNAMIC_IMPORT — while the lazy import
// below went on looking like it worked.
import { TradeChartResizeHandle } from '@root/widgets/trade/trade-chart/ui/desktop/trade-chart-handle'
import { TradeChartSkeleton } from '@root/widgets/trade/trade-chart/ui/desktop/trade-chart-skeleton'
import { useTradingChartResizing } from '@root/widgets/trade/trade-chart/ui/use-trade-chart-resizing'

import { chartHeightAtom } from './atoms'
import { tradesTableViewOptionAtom } from './atoms/reatom-trading-tables'
import { TradesTableViewOption } from './model/trades-table-view-option'
import { TradesTable } from './ui/trade-tables/trades-table'

import type { TradesTableModel } from './atoms/reatom-trades-table'
import type {
  BuyControlsModel,
  SellControlsModel,
} from '@root/features/trade/trading-controls/atoms/common'
import type { ClassNameProps } from '@root/shared/react'

type TradePageProps = {
  buyControlsModel: BuyControlsModel
  sellControlsModel: SellControlsModel
  tradesTableModel: TradesTableModel | undefined
} & ClassNameProps

const ChartLazy = lazy(() =>
  import('@root/widgets/trade/trade-chart/ui/desktop/trade-chart').then(
    (mod) => ({
      default: mod.TradeChart,
    }),
  ),
)

export const TradePageChart: React.FC<TradePageProps> = (props) => {
  const { buyControlsModel, sellControlsModel, className, tradesTableModel } =
    props
  const [chartHeight] = useAtom(chartHeightAtom)

  const [state] = useAtom(tradingPairStateAtom)
  const [tradesTableViewOption] = useAtom(tradesTableViewOptionAtom)
  const toggleTradesTableViewOption = useAction(
    tradesTableViewOptionAtom.toggle,
  )
  const [pairAddress] = useAtom(tradingPairAddressAtom)

  const { bindHandle, heightMotionValue } = useTradingChartResizing({
    heightAtom: chartHeightAtom,
    minHeight: 250,
    maxHeight: window.innerHeight * 0.85,
  })

  return (
    <Root className={className}>
      <ChartRoot style={{ height: heightMotionValue }}>
        <Suspense fallback={<TradeChartSkeleton height={chartHeight} />}>
          {AsyncStateN.isSuccess(state) ? (
            <ChartLazy
              buyControlsModel={buyControlsModel}
              sellControlsModel={sellControlsModel}
              pair={state.value}
              isCompact={tradesTableViewOption === TradesTableViewOption.Chart}
            />
          ) : (
            <TradeChartSkeleton height={chartHeight} />
          )}
        </Suspense>
        <InPlaceTogglerContainer>
          <InPlaceToggler
            $isOpen={tradesTableViewOption === TradesTableViewOption.Chart}
            type="button"
            onClick={() => toggleTradesTableViewOption()}
          >
            <ChevronLeftIcon size={16} />
          </InPlaceToggler>
        </InPlaceTogglerContainer>
        <AnimatePresence>
          {tradesTableViewOption === TradesTableViewOption.Chart && (
            <MotionSidebar
              key="sidebar"
              initial={{ width: 0 }}
              animate={{ width: 350 }}
              exit={{ width: 0 }}
              transition={{ duration: 0.2 }}
            >
              {AsyncStateN.matchWaiting(state, {
                Success: (pair) =>
                  tradesTableModel ? (
                    <TradesTable
                      compact
                      model={tradesTableModel}
                      pair={pair}
                      pairAddress={pairAddress}
                    />
                  ) : (
                    <OrdersTablesSkeleton columnsCount={4} />
                  ),
                Waiting: () => <OrdersTablesSkeleton columnsCount={4} />,
                Error: () => <OrdersTablesSkeleton columnsCount={4} />,
              })}
            </MotionSidebar>
          )}
        </AnimatePresence>
      </ChartRoot>
      <TradeChartResizeHandle {...bindHandle()} />
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  isolation: isolate;
`

const ChartRoot = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-height: 0;
  overflow: hidden;
  display: grid;

  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-rows: minmax(0, 1fr);
  align-items: stretch;

  ${mediaMaxTablet(css`
    border-end-end-radius: 14px;
    border-end-start-radius: 14px;
  `)}
`

const MotionSidebar = styled(motion.div)`
  display: grid;
  border-left: 1px solid var(--background-3);
  overflow: hidden;
  position: relative;
`
const InPlaceTogglerContainer = styled.div`
  width: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
`
const InPlaceToggler = styled.button<{ $isOpen: boolean }>`
  all: unset;
  z-index: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-9);
  width: 9px;
  height: 35px;
  border-radius: 8px;
  ${({ $isOpen }) =>
    $isOpen
      ? css`
          background: var(--background-3);
          color: var(--text-10);
          transform: translateX(0);
          > svg {
            transform: rotate(180deg);
          }
        `
      : css`
          background: var(--background-2);
          transform: translateX(-50%);

          &:hover {
            transform: translateX(-20%);
          }
        `}

  transform: translateX(0);
  transition: transform 0.1s ease-in-out;

  &:hover,
  &:focus {
    background: var(--background-4);
    color: var(--text-11);
  }
`
